import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon, TableCellsIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { usePermissions } from '../../contexts/PermissionContext';
import { tablesByGroup, isTableReady, type TableGroup, type DataTableDef } from '../../config/dataTables.registry';

/**
 * Data Management hub — the single landing page for every maintenance table,
 * grouped by subcategory (CMS / Quote / Safety / Admin) and filtered to what
 * the current user can reach (`hasAccess` on each table's route — the same gate
 * the sidebar used). An on-page search filters the cards. Only "ready" tables
 * (live routes) are shown, so not-yet-wired engine tables don't surface dead links.
 *
 * The page itself is ungated: it exposes nothing a user can't already access,
 * since every card is `hasAccess`-filtered and links to its own gated route.
 */

// Left-border accent per group, so the buckets read as distinct at a glance.
const GROUP_ACCENT: Record<TableGroup, string> = {
  cms: 'border-l-blue-500',
  quote: 'border-l-indigo-500',
  safety: 'border-l-amber-500',
  locations: 'border-l-emerald-500',
  warehouse: 'border-l-stone-500',
  carriers: 'border-l-cyan-500',
  equipment: 'border-l-teal-500',
  rates: 'border-l-green-500',
  billing: 'border-l-rose-500',
  driver: 'border-l-orange-500',
  driverpay: 'border-l-yellow-500',
  tracing: 'border-l-purple-500',
  integrations: 'border-l-fuchsia-500',
  reports: 'border-l-sky-500',
  admin: 'border-l-slate-400',
};

// Per-subgroup left-border overrides, so a related sub-family reads distinctly from its
// parent group's cards (e.g. the CMS "Steamship Line" SCAC tables use sky, not CMS blue).
const SUBGROUP_ACCENT: Record<string, string> = {
  'Steamship Line': 'border-l-sky-500',
};

const DataManagementHub: React.FC = () => {
  const { hasAccess } = usePermissions();
  const [search, setSearch] = useState('');

  const groups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tablesByGroup()
      .map((g) => ({
        ...g,
        tables: g.tables.filter(
          (t) =>
            isTableReady(t) &&
            hasAccess(t.route) &&
            (q === '' || t.label.toLowerCase().includes(q)),
        ),
      }))
      .filter((g) => g.tables.length > 0);
  }, [search, hasAccess]);

  const totalVisible = groups.reduce((n, g) => n + g.tables.length, 0);

  // A responsive grid of table cards — reused for a group's top-level cards and
  // for each subgroup section beneath it.
  const renderCards = (tables: DataTableDef[]) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {tables.map((t) => (
        <Link
          key={t.key}
          to={t.route}
          className={`group flex items-start gap-3 rounded-lg border border-l-4 border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-blue-400 hover:bg-blue-50/50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-500 dark:hover:bg-gray-700/50 ${(t.subgroup && SUBGROUP_ACCENT[t.subgroup]) || GROUP_ACCENT[t.group]}`}
        >
          <TableCellsIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-blue-500" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-body">
              {t.label}
            </div>
            {t.description && (
              <div className="mt-0.5 truncate text-xs text-muted">
                {t.description}
              </div>
            )}
          </div>
          <ChevronRightIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300 group-hover:text-blue-500 dark:text-gray-600" />
        </Link>
      ))}
    </div>
  );

  // Split a group's (already access/search-filtered) tables into the no-subgroup
  // cards plus subgroup buckets. Cards are sorted alphabetically by label (within the
  // plain list and within each subgroup); the subgroup *sections* stay in first-seen
  // registry order so curated ordering (e.g. Integrations → EDI / Melrose / Upload) holds.
  const byLabel = (a: DataTableDef, b: DataTableDef) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  const partitionSubgroups = (tables: DataTableDef[]) => {
    const plain = tables.filter((t) => !t.subgroup).sort(byLabel);
    const subNames: string[] = [];
    const subMap = new Map<string, DataTableDef[]>();
    tables.forEach((t) => {
      if (!t.subgroup) return;
      if (!subMap.has(t.subgroup)) {
        subMap.set(t.subgroup, []);
        subNames.push(t.subgroup);
      }
      subMap.get(t.subgroup)!.push(t);
    });
    subMap.forEach((bucket) => bucket.sort(byLabel));
    return { plain, subNames, subMap };
  };

  return (
    <div className="h-full w-full overflow-auto p-10 pb-24">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-body">Table Maintenance</h1>
        <p className="mt-1 text-sm text-muted">
          Maintain the lookup and configuration tables you have access to.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-8 max-w-md">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter tables…"
          className="block w-full rounded-md border-0 bg-panel py-2 pl-10 pr-10 text-body shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:ring-gray-700 sm:text-sm"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Clear filter"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {totalVisible === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-700">
          <p className="text-sm text-muted">
            {search
              ? `No tables match "${search}".`
              : 'You don’t have access to any tables.'}
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {groups.map((g) => {
            const { plain, subNames, subMap } = partitionSubgroups(g.tables);
            return (
              <section key={g.group}>
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted">
                  {g.label}
                  <span className="ml-2 font-normal text-gray-400 dark:text-gray-500">({g.tables.length})</span>
                </h2>
                {plain.length > 0 && renderCards(plain)}
                {subNames.map((name, idx) => (
                  <div key={name} className={idx === 0 && plain.length === 0 ? '' : 'mt-5'}>
                    <h3 className="mb-2 text-[0.6875rem] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      {name}
                    </h3>
                    {renderCards(subMap.get(name)!)}
                  </div>
                ))}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DataManagementHub;
